import { useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import type { SortOption, WorkerTerminalError } from '../types/worker'

/**
 * One line of a delivery the shop is about to verify.
 *
 * `expected_qty` is what the warehouse actually transferred, so it is what
 * should physically be in the box. `original_qty` is what the customer
 * reserved. They differ only when the warehouse could not send the full
 * amount, which is exactly the case step 2 has to be able to surface: a line
 * with `expected_qty === 0` never left the warehouse at all, so there is
 * nothing for step 1 to verify, but it still belongs on the original list.
 */
export interface ShopReceiptQueueLine {
	item_id: string
	product_id: string
	sku: string | null
	name: string | null
	image_url: string | null
	reference_code: string | null
	barcode: string | null
	color: string
	size: string
	expected_qty: number
	original_qty: number
}

export interface ShopReceiptQueueItem {
	reservation_id: string
	reservation_number: string
	customer_name: string
	status: string
	fulfilled_at: string | null
	source: string
	source_order_number: string | null
	is_store_pickup: boolean
	worker_name: string | null
	receipt_id: string | null
	receipt_status: string | null
	items: ShopReceiptQueueLine[]
	item_count: number
	unit_count: number
}

interface UseShopReceiptQueueConfig {
	adminId: string | null
	pageSize?: number
	sortBy?: SortOption
}

interface UseShopReceiptQueueReturn {
	deliveries: ShopReceiptQueueItem[]
	loading: boolean
	error: WorkerTerminalError | null
	totalCount: number
	currentPage: number
	hasMore: boolean
	loadDeliveries: (page?: number) => Promise<void>
	loadMore: () => Promise<void>
	refresh: () => Promise<void>
	clearError: () => void
}

function parseError(err: unknown): WorkerTerminalError {
	if (err instanceof Error) {
		const message = err.message.toLowerCase()
		if (message.includes('network') || message.includes('fetch') || message.includes('connection')) {
			return {
				type: 'connection',
				message: 'Unable to connect. Please check your internet connection.',
				retryable: true,
			}
		}
		if (message.includes('auth') || message.includes('jwt') || message.includes('token')) {
			return { type: 'auth', message: 'Your session has expired. Please log in again.', retryable: false }
		}
	}
	return { type: 'data', message: 'Unable to load deliveries. Please try again.', retryable: true }
}

/** Deliveries the warehouse has fulfilled that the shop has not yet confirmed. */
export function useShopReceiptQueue({
	adminId,
	pageSize = 25,
	sortBy = 'oldest',
}: UseShopReceiptQueueConfig): UseShopReceiptQueueReturn {
	const [deliveries, setDeliveries] = useState<ShopReceiptQueueItem[]>([])
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<WorkerTerminalError | null>(null)
	const [totalCount, setTotalCount] = useState(0)
	const [currentPage, setCurrentPage] = useState(0)

	const loadDeliveries = useCallback(
		async (page: number = 0) => {
			if (!adminId) return

			setLoading(true)
			setError(null)

			try {
				const { data, error: rpcError } = await supabase.rpc('get_shop_receipts_to_confirm', {
					p_admin_id: adminId,
					p_limit: pageSize,
					p_offset: page * pageSize,
					p_sort_by: sortBy,
				})

				if (rpcError) throw rpcError

				if (data && data.length > 0) {
					const mapped: ShopReceiptQueueItem[] = data.map((row: Record<string, unknown>) => ({
						reservation_id: row.reservation_id as string,
						reservation_number: row.reservation_number as string,
						customer_name: (row.customer_name as string) || '',
						status: row.status as string,
						fulfilled_at: (row.fulfilled_at as string) ?? null,
						source: (row.source as string) || 'manual',
						source_order_number: (row.source_order_number as string) ?? null,
						is_store_pickup: Boolean(row.is_store_pickup),
						worker_name: (row.worker_name as string) ?? null,
						receipt_id: (row.receipt_id as string) ?? null,
						receipt_status: (row.receipt_status as string) ?? null,
						items: Array.isArray(row.items) ? (row.items as ShopReceiptQueueLine[]) : [],
						item_count: (row.item_count as number) ?? 0,
						unit_count: (row.unit_count as number) ?? 0,
					}))

					if (page === 0) setDeliveries(mapped)
					else setDeliveries((prev) => [...prev, ...mapped])

					setTotalCount(Number(data[0].total_count) || 0)
					setCurrentPage(page)
				} else if (page === 0) {
					setDeliveries([])
					setTotalCount(0)
				}
			} catch (err) {
				console.error('Failed to load shop deliveries:', err)
				setError(parseError(err))
			} finally {
				setLoading(false)
			}
		},
		[adminId, pageSize, sortBy]
	)

	const loadMore = useCallback(async () => {
		const loadedCount = (currentPage + 1) * pageSize
		if (loadedCount >= totalCount) return
		await loadDeliveries(currentPage + 1)
	}, [currentPage, pageSize, totalCount, loadDeliveries])

	const refresh = useCallback(async () => {
		setDeliveries([])
		setCurrentPage(0)
		await loadDeliveries(0)
	}, [loadDeliveries])

	const clearError = useCallback(() => setError(null), [])

	return {
		deliveries,
		loading,
		error,
		totalCount,
		currentPage,
		hasMore: deliveries.length < totalCount,
		loadDeliveries,
		loadMore,
		refresh,
		clearError,
	}
}
